import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar, Doughnut, Line, Pie } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

import api, { reportService, referenceService } from '../../services/api';

const Reports = () => {
  const [activeReport, setActiveReport] = useState('tenders');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    tenderStats: { labels: [], data: [] },
    quotationStats: { labels: [], data: [] },
    projectStats: { labels: [], data: [] },
    projectStats: { labels: [], data: [] },
    earnestMoneyStats: { total: 0, released: 0, pending: 0 },
    performanceSecurityStats: { total: 0, released: 0, pending: 0 },
    tenders: []
  });

  const [salesData, setSalesData] = useState({
    pendingInvoices: [],
    unbilledDeliveries: [],
    customerSales: [],
    salesTaxInvoices: []
  });
  const [salesLoading, setSalesLoading] = useState(false);

  const [stockMovementData, setStockMovementData] = useState({
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      { label: 'Stock Inflow', data: [0, 0, 0, 0, 0, 0], borderColor: 'rgb(16, 185, 129)', backgroundColor: 'rgba(16, 185, 129, 0.1)', borderWidth: 3, fill: true, tension: 0.4 },
      { label: 'Stock Outflow', data: [0, 0, 0, 0, 0, 0], borderColor: 'rgb(239, 68, 68)', backgroundColor: 'rgba(239, 68, 68, 0.1)', borderWidth: 3, fill: true, tension: 0.4 }
    ]
  });

  const [pnlData, setPnlData] = useState([]);
  const [pnlLoading, setPnlLoading] = useState(false);

  // Helper to handle expense input change
  const handleExpenseChange = (id, value) => {
    setPnlData(prev => prev.map(item =>
      item.id === id ? { ...item, expenseCost: value } : item
    ));
  };

  // Helper to save expense and recalculate local stats
  const saveExpense = async (id, value) => {
    try {
      await referenceService.updateProject(id, { expenseCost: value });
      // Recalculate locally to reflect changes immediately without reload
      setPnlData(prev => prev.map(p => {
        if (p.id !== id) return p;
        const exp = parseFloat(value || 0);
        const profit = p.quotedCost - exp;
        let margin = 0;
        if (p.quotedCost !== 0) {
          margin = ((profit / p.quotedCost) * 100); // keep as number for now
          margin = Math.round(margin * 100) / 100; // round to 2 decimals
        }
        return { ...p, expenseCost: exp, profit, margin };
      }));
      // Optional: Success toast? No, too noisy for a table edit.
    } catch (error) {
      console.error('Failed to save expense:', error);
      Swal.fire('Error', 'Failed to save expense cost', 'error');
    }
  };

  useEffect(() => {
    if (activeReport === 'pnl') {
      const loadPnl = async () => {
        setPnlLoading(true);
        try {
          const res = await api.get('/reports/pnl');
          setPnlData(res.data);
        } catch (error) {
          console.error('Error loading P&L:', error);
        } finally {
          setPnlLoading(false);
        }
      };
      loadPnl();
    }
    if (activeReport === 'sales') {
      const loadSalesData = async () => {
        setSalesLoading(true);
        try {
          const res = await api.get('/reports/sales');
          setSalesData(res.data);
        } catch (error) {
          console.error('Error fetching sales reports:', error);
        } finally {
          setSalesLoading(false);
        }
      };
      loadSalesData();
    }
  }, [activeReport]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await reportService.getStats();
        setStats(response.data);

        if (response.data.stockMovement) {
          setStockMovementData({
            labels: response.data.stockMovement.labels,
            datasets: [
              { label: 'Stock Inflow', data: response.data.stockMovement.inflow, borderColor: 'rgb(16, 185, 129)', backgroundColor: 'rgba(16, 185, 129, 0.1)', borderWidth: 3, fill: true, tension: 0.4 },
              { label: 'Stock Outflow', data: response.data.stockMovement.outflow, borderColor: 'rgb(239, 68, 68)', backgroundColor: 'rgba(239, 68, 68, 0.1)', borderWidth: 3, fill: true, tension: 0.4 }
            ]
          });
        }
      } catch (error) {
        console.error("Error fetching reports:", error);
        Swal.fire('Error', 'Failed to load report data', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const handleExportTendersCSV = async () => {
    try {
      Swal.fire({
        title: 'Exporting CSV...',
        text: 'Please wait...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });
      const response = await reportService.exportTendersCSV();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'tender_report.csv');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      Swal.close();
    } catch (error) {
      console.error('Export CSV failed', error);
      Swal.fire('Error', 'Failed to export CSV', 'error');
    }
  };

  const handleExportTendersPDF = async () => {
    try {
      Swal.fire({
        title: 'Exporting PDF...',
        text: 'Please wait...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });
      const response = await reportService.exportTendersPDF();
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'tender_report.pdf');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      Swal.close();
    } catch (error) {
      console.error('Export PDF failed', error);
      Swal.fire('Error', 'Failed to export PDF', 'error');
    }
  };

  const handleExportSales = async () => {
    try {
      Swal.fire({ title: 'Exporting CSV...', didOpen: () => Swal.showLoading() });
      const response = await reportService.exportSales();
      downloadBlob(response.data, 'sales_report.csv');
      Swal.close();
    } catch (error) { Swal.fire('Error', 'Failed to export sales report', 'error'); }
  };

  const handleExportSalesPDF = async () => {
    try {
      Swal.fire({ title: 'Exporting PDF...', didOpen: () => Swal.showLoading() });
      const response = await reportService.exportSalesPDF();
      downloadBlob(response.data, 'sales_report.pdf');
      Swal.close();
    } catch (error) { Swal.fire('Error', 'Failed to export sales PDF', 'error'); }
  };

  const handleExportQuotations = async (type) => {
    try {
      Swal.fire({ title: `Exporting ${type}...`, didOpen: () => Swal.showLoading() });
      const response = type === 'CSV' ? await reportService.exportQuotationsCSV() : await reportService.exportQuotationsPDF();
      downloadBlob(response.data, `quotations_report.${type.toLowerCase()}`);
      Swal.close();
    } catch (e) { Swal.fire('Error', 'Export failed', 'error'); }
  };

  const handleExportPnL = async (type) => {
    try {
      Swal.fire({ title: `Exporting ${type}...`, didOpen: () => Swal.showLoading() });
      const response = type === 'CSV' ? await reportService.exportPnLCSV() : await reportService.exportPnLPDF();
      downloadBlob(response.data, `pnl_report.${type.toLowerCase()}`);
      Swal.close();
    } catch (e) { Swal.fire('Error', 'Export failed', 'error'); }
  };

  const handleExportStock = async (type) => {
    try {
      Swal.fire({ title: `Exporting ${type}...`, didOpen: () => Swal.showLoading() });
      const response = type === 'CSV' ? await reportService.exportStockCSV() : await reportService.exportStockPDF();
      downloadBlob(response.data, `stock_report.${type.toLowerCase()}`);
      Swal.close();
    } catch (e) { Swal.fire('Error', 'Export failed', 'error'); }
  };

  const handleExportAll = async () => {
    try {
      Swal.fire({ title: 'Generating Zip...', text: 'This may take a moment', didOpen: () => Swal.showLoading() });
      const response = await reportService.exportAllZip();
      downloadBlob(response.data, 'all_reports.zip');
      Swal.close();
    } catch (e) { Swal.fire('Error', 'Export failed', 'error'); }
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(new Blob([blob]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  const tenderStageData = {
    labels: stats.tenderStats.labels,
    datasets: [
      {
        label: 'Tenders',
        data: stats.tenderStats.data,
        backgroundColor: [
          'rgba(107, 114, 128, 0.9)',
          'rgba(59, 130, 246, 0.9)',
          'rgba(16, 185, 129, 0.9)',
          'rgba(102, 126, 234, 0.9)',
          'rgba(245, 158, 11, 0.9)',
          'rgba(239, 68, 68, 0.9)',
          'rgba(31, 41, 55, 0.9)',
        ],
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  };

  const quotationStatusData = {
    labels: stats.quotationStats.labels,
    datasets: [
      {
        label: 'Quotations',
        data: stats.quotationStats.data,
        backgroundColor: [
          'rgba(107, 114, 128, 0.9)',
          'rgba(59, 130, 246, 0.9)',
          'rgba(102, 126, 234, 0.9)',
          'rgba(245, 158, 11, 0.9)',
          'rgba(16, 185, 129, 0.9)',
          'rgba(239, 68, 68, 0.9)',
          'rgba(220, 38, 38, 0.9)',
        ],
        borderWidth: 0,
        hoverOffset: 8,
      },
    ],
  };

  // Stock Movement Data (Keeping Mock for now until backend implementation is more robust)
  // ... using state variable

  const projectProgressData = {
    labels: stats.projectStats.labels,
    datasets: [
      {
        label: 'Projects',
        data: stats.projectStats.data,
        backgroundColor: [
          'rgba(59, 130, 246, 0.9)',
          'rgba(16, 185, 129, 0.9)',
          'rgba(245, 158, 11, 0.9)',
          'rgba(107, 114, 128, 0.9)',
          'rgba(239, 68, 68, 0.9)',
        ],
        borderWidth: 0,
        hoverOffset: 8,
      },
    ],
  };

  const recordTypeData = {
    labels: ['Letters', 'Quotations', 'Invoices', 'Contracts', 'Others'],
    datasets: [
      {
        label: 'Records',
        data: [150, 80, 120, 45, 30], // Still mock until Record module is fully defined
        backgroundColor: [
          'rgba(102, 126, 234, 0.9)',
          'rgba(118, 75, 162, 0.9)',
          'rgba(16, 185, 129, 0.9)',
          'rgba(59, 130, 246, 0.9)',
          'rgba(107, 114, 128, 0.9)',
        ],
        borderWidth: 0,
        hoverOffset: 8,
      },
    ],
  };

  // Tenders Data for Earnest Money Table
  const tendersData = stats.tenders || [];

  // Calculate Granular Earnest Money Stats locally
  const emStats = tendersData.reduce((acc, t) => {
    const amount = parseFloat(t.earnestMoneyAmount || 0);
    if (amount > 0) {
      acc.total += amount;
      if (t.earnestMoneyReleased) {
        acc.released += amount;
      } else {
        // Check for specific stage "Release Earnest Money" (Stage 21)
        if (t.currentStage === 21) {
          acc.pendingRelease += amount;
        } else {
          acc.inProcess += amount;
        }
      }
    }
    return acc;
  }, { total: 0, released: 0, inProcess: 0, pendingRelease: 0 });

  const performanceSecurityStats = stats.performanceSecurityStats || { total: 0, released: 0, pending: 0 };

  // Common chart options for consistent sizing
  const commonChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 2,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: 12,
            weight: '600'
          },
          padding: 15,
          usePointStyle: true,
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: '600'
        },
        bodyFont: {
          size: 13
        },
        cornerRadius: 8,
      }
    },
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart'
    }
  };

  // Doughnut/Pie chart specific options
  const doughnutChartOptions = {
    ...commonChartOptions,
    aspectRatio: 1.5,
    plugins: {
      ...commonChartOptions.plugins,
      legend: {
        position: 'bottom',
        labels: {
          font: {
            size: 12,
            weight: '600'
          },
          padding: 15,
          usePointStyle: true,
        }
      }
    }
  };

  // Bar chart specific options
  const barChartOptions = {
    ...commonChartOptions,
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          font: {
            size: 11
          }
        }
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11
          }
        }
      }
    }
  };

  // Line chart specific options
  const lineChartOptions = {
    ...commonChartOptions,
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      x: {
        grid: {
          display: false,
        }
      }
    },
    elements: {
      point: {
        radius: 5,
        hoverRadius: 7,
      },
      line: {
        tension: 0.4,
        borderWidth: 3,
      }
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-4">
        <h2 className="mb-1" style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          fontWeight: '700',
          fontSize: '2rem'
        }}>
          Reports & Analytics
        </h2>
        <p className="text-muted mb-0">Comprehensive insights and analytics for your business</p>
      </div>

      <ul className="nav nav-tabs mb-4" style={{ borderBottom: '2px solid var(--gray-200)' }}>
        <li className="nav-item">
          <button
            className={`nav-link ${activeReport === 'tenders' ? 'active' : ''}`}
            onClick={() => setActiveReport('tenders')}
            style={{
              border: 'none',
              borderBottom: activeReport === 'tenders' ? '3px solid #667eea' : '3px solid transparent',
              fontWeight: activeReport === 'tenders' ? '600' : '500',
              color: activeReport === 'tenders' ? '#667eea' : 'var(--gray-600)',
              transition: 'all 0.3s ease',
              borderRadius: '0'
            }}
          >
            <i className="bi bi-file-earmark-check me-2"></i>Tender Reports
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeReport === 'quotations' ? 'active' : ''}`}
            onClick={() => setActiveReport('quotations')}
            style={{
              border: 'none',
              borderBottom: activeReport === 'quotations' ? '3px solid #667eea' : '3px solid transparent',
              fontWeight: activeReport === 'quotations' ? '600' : '500',
              color: activeReport === 'quotations' ? '#667eea' : 'var(--gray-600)',
              transition: 'all 0.3s ease',
              borderRadius: '0'
            }}
          >
            <i className="bi bi-file-earmark-text me-2"></i>Quotation Reports
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeReport === 'stock' ? 'active' : ''}`}
            onClick={() => setActiveReport('stock')}
            style={{
              border: 'none',
              borderBottom: activeReport === 'stock' ? '3px solid #667eea' : '3px solid transparent',
              fontWeight: activeReport === 'stock' ? '600' : '500',
              color: activeReport === 'stock' ? '#667eea' : 'var(--gray-600)',
              transition: 'all 0.3s ease',
              borderRadius: '0'
            }}
          >
            <i className="bi bi-box-seam me-2"></i>Stock Reports
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeReport === 'projects' ? 'active' : ''}`}
            onClick={() => setActiveReport('projects')}
            style={{
              border: 'none',
              borderBottom: activeReport === 'projects' ? '3px solid #667eea' : '3px solid transparent',
              fontWeight: activeReport === 'projects' ? '600' : '500',
              color: activeReport === 'projects' ? '#667eea' : 'var(--gray-600)',
              transition: 'all 0.3s ease',
              borderRadius: '0'
            }}
          >
            <i className="bi bi-folder me-2"></i>Project Reports
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeReport === 'records' ? 'active' : ''}`}
            onClick={() => setActiveReport('records')}
            style={{
              border: 'none',
              borderBottom: activeReport === 'records' ? '3px solid #667eea' : '3px solid transparent',
              fontWeight: activeReport === 'records' ? '600' : '500',
              color: activeReport === 'records' ? '#667eea' : 'var(--gray-600)',
              transition: 'all 0.3s ease',
              borderRadius: '0'
            }}
          >
            <i className="bi bi-file-earmark-text me-2"></i>Record Reports
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeReport === 'earnestMoney' ? 'active' : ''}`}
            onClick={() => setActiveReport('earnestMoney')}
            style={{
              border: 'none',
              borderBottom: activeReport === 'earnestMoney' ? '3px solid #667eea' : '3px solid transparent',
              fontWeight: activeReport === 'earnestMoney' ? '600' : '500',
              color: activeReport === 'earnestMoney' ? '#667eea' : 'var(--gray-600)',
              transition: 'all 0.3s ease',
              borderRadius: '0'
            }}
          >
            <i className="bi bi-cash-coin me-2"></i>Earnest Money
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeReport === 'pnl' ? 'active' : ''}`}
            onClick={() => setActiveReport('pnl')}
            style={{
              border: 'none',
              borderBottom: activeReport === 'pnl' ? '3px solid #667eea' : '3px solid transparent',
              fontWeight: activeReport === 'pnl' ? '600' : '500',
              color: activeReport === 'pnl' ? '#667eea' : 'var(--gray-600)',
              transition: 'all 0.3s ease',
              borderRadius: '0'
            }}
          >
            <i className="bi bi-graph-up-arrow me-2"></i>Project Profit & Loss
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeReport === 'performanceSecurity' ? 'active' : ''}`}
            onClick={() => setActiveReport('performanceSecurity')}
            style={{
              border: 'none',
              borderBottom: activeReport === 'performanceSecurity' ? '3px solid #667eea' : '3px solid transparent',
              fontWeight: activeReport === 'performanceSecurity' ? '600' : '500',
              color: activeReport === 'performanceSecurity' ? '#667eea' : 'var(--gray-600)',
              transition: 'all 0.3s ease',
              borderRadius: '0'
            }}
          >
            <i className="bi bi-shield-check me-2"></i>Performance Security
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeReport === 'sales' ? 'active' : ''}`}
            onClick={() => setActiveReport('sales')}
            style={{
              border: 'none',
              borderBottom: activeReport === 'sales' ? '3px solid #667eea' : '3px solid transparent',
              fontWeight: activeReport === 'sales' ? '600' : '500',
              color: activeReport === 'sales' ? '#667eea' : 'var(--gray-600)',
              transition: 'all 0.3s ease',
              borderRadius: '0'
            }}
          >
            <i className="bi bi-graph-up-arrow me-2"></i>Sales & Finance
          </button>
        </li>
      </ul>

      {activeReport === 'tenders' && (
        <div className="row g-4">
          <div className="col-12">
            <div className="card animate-slide-in-left">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="bi bi-bar-chart me-2"></i>Tender Stage Distribution
                </h5>
              </div>
              <div className="card-body" style={{ padding: '24px', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: '100%', maxWidth: '100%', height: '350px' }}>
                  <Bar data={tenderStageData} options={barChartOptions} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeReport === 'quotations' && (
        <div className="row g-4">
          <div className="col-12">
            <div className="card animate-slide-in-left">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="bi bi-pie-chart me-2"></i>Quotation Status Distribution
                </h5>
              </div>
              <div className="card-body" style={{ padding: '24px', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: '100%', maxWidth: '600px', height: '350px', margin: '0 auto' }}>
                  <Doughnut data={quotationStatusData} options={doughnutChartOptions} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeReport === 'stock' && (
        <div className="row g-4">
          <div className="col-12">
            <div className="card animate-slide-in-left">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="bi bi-graph-up me-2"></i>Stock Movement Trend
                </h5>
              </div>
              <div className="card-body" style={{ padding: '24px', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: '100%', maxWidth: '100%', height: '350px' }}>
                  <Line data={stockMovementData} options={lineChartOptions} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeReport === 'projects' && (
        <div className="row g-4">
          <div className="col-12">
            <div className="card animate-slide-in-left">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="bi bi-pie-chart me-2"></i>Project Status Distribution
                </h5>
              </div>
              <div className="card-body" style={{ padding: '24px', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: '100%', maxWidth: '600px', height: '350px', margin: '0 auto' }}>
                  <Pie data={projectProgressData} options={doughnutChartOptions} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeReport === 'records' && (
        <div className="row g-4">
          <div className="col-12">
            <div className="card animate-slide-in-left">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="bi bi-pie-chart me-2"></i>Record Type Distribution
                </h5>
              </div>
              <div className="card-body" style={{ padding: '24px', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: '100%', maxWidth: '600px', height: '350px', margin: '0 auto' }}>
                  <Doughnut data={recordTypeData} options={doughnutChartOptions} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeReport === 'earnestMoney' && (
        <div className="animate-slide-in-left">
          {/* Summary Cards */}
          <div className="row g-4 mb-4">
            <div className="col-md-3">
              <div className="card text-center h-100 border-primary">
                <div className="card-body">
                  <h6 className="text-muted text-uppercase mb-2">Total Earnest Money Paid</h6>
                  <h3 className="text-primary fw-bold">Rs. {emStats.total.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card text-center h-100 border-success">
                <div className="card-body">
                  <h6 className="text-muted text-uppercase mb-2">Total Released (Refunded)</h6>
                  <h3 className="text-success fw-bold">Rs. {emStats.released.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card text-center h-100 border-info">
                <div className="card-body">
                  <h6 className="text-muted text-uppercase mb-2">In Process</h6>
                  <h3 className="text-info fw-bold">Rs. {emStats.inProcess.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card text-center h-100 border-warning">
                <div className="card-body">
                  <h6 className="text-muted text-uppercase mb-2">Pending Release</h6>
                  <h3 className="text-warning fw-bold">Rs. {emStats.pendingRelease.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</h3>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-4">
            {/* Chart Section */}
            {/* Chart Section */}
            <div className="col-lg-5">
              <div className="card h-100">
                <div className="card-header">
                  <h5 className="mb-0">
                    <i className="bi bi-pie-chart me-2"></i>Release Status
                  </h5>
                </div>
                <div className="card-body d-flex align-items-center justify-content-center">
                  <div style={{ width: '100%', maxWidth: '350px', height: '300px' }}>
                    <Doughnut
                      data={{
                        labels: ['Released', 'In Process', 'Pending Release'],
                        datasets: [{
                          data: [emStats.released, emStats.inProcess, emStats.pendingRelease],
                          backgroundColor: [
                            'rgba(16, 185, 129, 0.9)', // Released - Green
                            'rgba(59, 130, 246, 0.9)', // In Process - Blue (Info)
                            'rgba(245, 158, 11, 0.9)'  // Pending Release - Orange (Warning)
                          ],
                          borderWidth: 0,
                          hoverOffset: 4
                        }]
                      }}
                      options={doughnutChartOptions}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Table Section */}
            <div className="col-lg-7">
              <div className="card h-100">
                <div className="card-header">
                  <h5 className="mb-0">
                    <i className="bi bi-table me-2"></i>Tender Wise Earnest Money
                  </h5>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0 align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>Tender No</th>
                          <th>Organization</th>
                          <th className="text-end">Amount</th>
                          <th className="text-center">Status</th>
                          <th>Release Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tendersData.filter(t => t.earnestMoneyAmount > 0).map(tender => {
                          let statusBadge;
                          if (tender.earnestMoneyReleased) {
                            statusBadge = <span className="badge bg-success rounded-pill">Released</span>;
                          } else if (tender.currentStage === 21) {
                            statusBadge = <span className="badge bg-warning text-dark rounded-pill">Pending Release</span>;
                          } else {
                            statusBadge = <span className="badge bg-info text-dark rounded-pill">In Process</span>;
                          }

                          return (
                            <tr key={tender.id}>
                              <td className="fw-bold text-primary">{tender.tenderNo}</td>
                              <td>{tender.organization}</td>
                              <td className="text-end fw-bold">
                                Rs. {tender.earnestMoneyAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                              </td>
                              <td className="text-center">
                                {statusBadge}
                              </td>
                              <td className="text-muted small">
                                {tender.earnestMoneyReleased ? tender.earnestMoneyReleaseDate : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeReport === 'performanceSecurity' && (
        <div className="animate-slide-in-left">
          {/* Summary Cards */}
          <div className="row g-4 mb-4">
            <div className="col-md-4">
              <div className="card text-center h-100 border-primary">
                <div className="card-body">
                  <h6 className="text-muted text-uppercase mb-2">Total Performance Security</h6>
                  <h3 className="text-primary fw-bold">Rs. {performanceSecurityStats.total.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card text-center h-100 border-success">
                <div className="card-body">
                  <h6 className="text-muted text-uppercase mb-2">Total Released (Completed)</h6>
                  <h3 className="text-success fw-bold">Rs. {performanceSecurityStats.released.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card text-center h-100 border-warning">
                <div className="card-body">
                  <h6 className="text-muted text-uppercase mb-2">Pending Release</h6>
                  <h3 className="text-warning fw-bold">Rs. {performanceSecurityStats.pending.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</h3>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-4">
            {/* Chart Section */}
            <div className="col-lg-5">
              <div className="card h-100">
                <div className="card-header">
                  <h5 className="mb-0">
                    <i className="bi bi-pie-chart me-2"></i>Release Status
                  </h5>
                </div>
                <div className="card-body d-flex align-items-center justify-content-center">
                  <div style={{ width: '100%', maxWidth: '350px', height: '300px' }}>
                    <Doughnut
                      data={{
                        labels: ['Released', 'Pending'],
                        datasets: [{
                          data: [performanceSecurityStats.released, performanceSecurityStats.pending],
                          backgroundColor: ['rgba(16, 185, 129, 0.9)', 'rgba(245, 158, 11, 0.9)'],
                          borderWidth: 0,
                          hoverOffset: 4
                        }]
                      }}
                      options={doughnutChartOptions}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Table Section */}
            <div className="col-lg-7">
              <div className="card h-100">
                <div className="card-header">
                  <h5 className="mb-0">
                    <i className="bi bi-table me-2"></i>Tender Wise Performance Security
                  </h5>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0 align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>Tender No</th>
                          <th>Organization</th>
                          <th className="text-end">Amount</th>
                          <th className="text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tendersData.filter(t => t.performanceSecurityAmount > 0).map(tender => {
                          const isReleased = (tender.currentStage === 14 || (tender.stageName && tender.stageName.toLowerCase().includes('closed')));
                          return (
                            <tr key={tender.id}>
                              <td className="fw-bold text-primary">{tender.tenderNo}</td>
                              <td>{tender.organization}</td>
                              <td className="text-end fw-bold">
                                Rs. {tender.performanceSecurityAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                              </td>
                              <td className="text-center">
                                {isReleased ? (
                                  <span className="badge bg-success rounded-pill">Released</span>
                                ) : (
                                  <span className="badge bg-warning text-dark rounded-pill">Pending</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeReport === 'pnl' && (
        <div className="animate-slide-in-left">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="bi bi-wallet2 me-2"></i>Project Profit & Loss Statements
              </h5>
              <button className="btn btn-sm btn-outline-primary" onClick={() => window.print()}>
                <i className="bi bi-printer me-2"></i>Print Report
              </button>
            </div>
            <div className="card-body">
              {pnlLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Project</th>
                        <th>Organization</th>
                        <th className="text-end">Revenue Source</th>
                        <th className="text-end">Quoted/Tender Cost</th>
                        <th className="text-end">Expense Cost</th>
                        <th className="text-end">Profit (Rs)</th>
                        <th className="text-end">Margin (%)</th>
                        <th className="text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pnlData.length === 0 ? (
                        <tr><td colSpan="8" className="text-center py-4">No data available</td></tr>
                      ) : (
                        pnlData.map(item => (
                          <tr key={item.id}>
                            <td className="fw-bold">{item.title}</td>
                            <td>{item.organization}</td>
                            <td className="text-end small text-muted">{item.revenueSource}</td>
                            <td className="text-end fw-bold text-primary">Rs. {(item.quotedCost || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>
                            <td className="text-end">
                              <input
                                type="number"
                                className="form-control form-control-sm text-end"
                                value={item.expenseCost}
                                onChange={(e) => handleExpenseChange(item.id, e.target.value)}
                                onBlur={(e) => saveExpense(item.id, e.target.value)}
                                style={{ minWidth: '80px' }}
                              />
                            </td>
                            <td className={`text-end fw-bold ${item.profit >= 0 ? 'text-success' : 'text-danger'}`}>
                              Rs. {(item.profit || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                            </td>
                            <td className="text-end">
                              <span className={`badge ${item.margin >= 20 ? 'bg-success' : item.margin > 0 ? 'bg-warning text-dark' : 'bg-danger'} rounded-pill`}>
                                {item.margin}%
                              </span>
                            </td>
                            <td className="text-center">
                              <span className="badge bg-secondary text-uppercase">{item.status}</span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    <tfoot className="table-light">
                      <tr>
                        <td colSpan="3" className="fw-bold text-end">Totals:</td>
                        <td className="text-end fw-bold text-primary">
                          Rs. {pnlData.reduce((acc, i) => acc + i.quotedCost, 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                        </td>
                        <td className="text-end fw-bold text-danger">
                          Rs. {pnlData.reduce((acc, i) => acc + i.expenseCost, 0).toLocaleString()}
                        </td>
                        <td className={`text-end fw-bold ${pnlData.reduce((acc, i) => acc + i.profit, 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                          Rs. {pnlData.reduce((acc, i) => acc + i.profit, 0).toLocaleString()}
                        </td>
                        <td colSpan="2"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* --- SALES & FINANCE TAB --- */}
      {activeReport === 'sales' && (
        <div className="animate-slide-in-left">
          {salesLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status"></div>
              <p className="mt-2 text-muted">Loading Sales Data...</p>
            </div>
          ) : (
            <>
              {/* Sales Summary Cards */}
              <div className="row g-4 mb-4">
                <div className="col-md-3">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body d-flex align-items-center">
                      <div className="bg-primary bg-opacity-10 p-3 rounded-circle me-3">
                        <i className="bi bi-receipt text-primary fs-3"></i>
                      </div>
                      <div>
                        <h6 className="text-muted text-uppercase mb-1 small">Pending Invoices</h6>
                        <h4 className="fw-bold mb-0">
                          Rs. {salesData.pendingInvoices.reduce((acc, inv) => acc + parseFloat(inv.netAmount || 0), 0).toLocaleString()}
                        </h4>
                        <small className="text-danger fw-bold">{salesData.pendingInvoices.length} Unpaid</small>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body d-flex align-items-center">
                      <div className="bg-warning bg-opacity-10 p-3 rounded-circle me-3">
                        <i className="bi bi-truck text-warning fs-3"></i>
                      </div>
                      <div>
                        <h6 className="text-muted text-uppercase mb-1 small">Unbilled Deliveries</h6>
                        <h4 className="fw-bold mb-0">
                          {salesData.unbilledDeliveries.length} DCs
                        </h4>
                        <small className="text-warning">Pending Invoice</small>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body d-flex align-items-center">
                      <div className="bg-success bg-opacity-10 p-3 rounded-circle me-3">
                        <i className="bi bi-graph-up text-success fs-3"></i>
                      </div>
                      <div>
                        <h6 className="text-muted text-uppercase mb-1 small">Total Sales</h6>
                        <h4 className="fw-bold mb-0">
                          Rs. {salesData.customerSales.reduce((acc, c) => acc + c.total, 0).toLocaleString()}
                        </h4>
                        <small className="text-success">Lifetime</small>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body d-flex align-items-center">
                      <div className="bg-secondary bg-opacity-10 p-3 rounded-circle me-3">
                        <i className="bi bi-bank text-secondary fs-3"></i>
                      </div>
                      <div>
                        <h6 className="text-muted text-uppercase mb-1 small">Tax Invoices</h6>
                        <h4 className="fw-bold mb-0">
                          {salesData.salesTaxInvoices.length} Generated
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row g-4 mb-4">
                {/* Unbilled Deliveries */}
                <div className="col-lg-6">
                  <div className="card h-100 border-0 shadow-sm">
                    <div className="card-header py-3">
                      <h6 className="fw-bold m-0"><i className="bi bi-exclamation-triangle me-2"></i>Unbilled Deliveries</h6>
                    </div>
                    <div className="card-body p-0">
                      <div className="table-responsive" style={{ maxHeight: '300px' }}>
                        <table className="table table-hover mb-0">
                          <thead className="table-light sticky-top">
                            <tr>
                              <th>Date</th>
                              <th>DC #</th>
                              <th>Client</th>
                              <th>Project</th>
                            </tr>
                          </thead>
                          <tbody>
                            {salesData.unbilledDeliveries.map(dc => (
                              <tr key={dc.id}>
                                <td>{new Date(dc.deliveryDate).toLocaleDateString()}</td>
                                <td>{dc.dcNumber}</td>
                                <td>{dc.Organization?.name}</td>
                                <td><small>{dc.Project?.title}</small></td>
                              </tr>
                            ))}
                            {salesData.unbilledDeliveries.length === 0 && (
                              <tr><td colSpan="4" className="text-center text-muted py-3">All deliveries invoiced</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pending Invoices */}
                <div className="col-lg-6">
                  <div className="card h-100 border-0 shadow-sm">
                    <div className="card-header py-3">
                      <h6 className="fw-bold m-0"><i className="bi bi-clock-history me-2"></i>Pending Invoices</h6>
                    </div>
                    <div className="card-body p-0">
                      <div className="table-responsive" style={{ maxHeight: '300px' }}>
                        <table className="table table-hover mb-0">
                          <thead className="table-light sticky-top">
                            <tr>
                              <th>Due Date</th>
                              <th>Inv #</th>
                              <th>Client</th>
                              <th className="text-end">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {salesData.pendingInvoices.map(inv => (
                              <tr key={inv.id}>
                                <td>{inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : '-'}</td>
                                <td>{inv.invoiceNumber}</td>
                                <td>{inv.Organization?.name}</td>
                                <td className="text-end fw-bold">{parseFloat(inv.netAmount).toLocaleString()}</td>
                              </tr>
                            ))}
                            {salesData.pendingInvoices.length === 0 && (
                              <tr><td colSpan="4" className="text-center text-muted py-3">No pending invoices</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row g-4">
                {/* Customer Sales Chart */}
                <div className="col-lg-5">
                  <div className="card h-100 border-0 shadow-sm">
                    <div className="card-header py-3">
                      <h6 className="fw-bold m-0"><i className="bi bi-pie-chart-fill me-2"></i>Top Customers by Revenue</h6>
                    </div>
                    <div className="card-body d-flex justify-content-center">
                      <div style={{ width: '300px', height: '300px' }}>
                        <Doughnut data={{
                          labels: salesData.customerSales.slice(0, 5).map(c => c.name),
                          datasets: [{
                            data: salesData.customerSales.slice(0, 5).map(c => c.total),
                            backgroundColor: ['#4e73df', '#1cc88a', '#36b9cc', '#f6c23e', '#e74a3b']
                          }]
                        }} options={{ maintainAspectRatio: false }} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sales Tax Register */}
                <div className="col-lg-7">
                  <div className="card h-100 border-0 shadow-sm">
                    <div className="card-header py-3">
                      <h6 className="fw-bold m-0"><i className="bi bi-files me-2"></i>Sales Tax Invoice Register</h6>
                    </div>
                    <div className="card-body p-0">
                      <div className="table-responsive" style={{ maxHeight: '350px' }}>
                        <table className="table table-hover mb-0">
                          <thead className="table-light sticky-top">
                            <tr>
                              <th>Date</th>
                              <th>ST Inv #</th>
                              <th>Inv #</th>
                              <th>Buyer NTN</th>
                              <th className="text-end">Tax Amt</th>
                            </tr>
                          </thead>
                          <tbody>
                            {salesData.salesTaxInvoices.map(sti => (
                              <tr key={sti.id}>
                                <td>{new Date(sti.invoiceDate).toLocaleDateString()}</td>
                                <td>{sti.stInvoiceNumber}</td>
                                <td>{sti.Invoice?.invoiceNumber}</td>
                                <td>{sti.buyerNTN || '-'}</td>
                                <td className="text-end">{parseFloat(sti.taxAmount).toLocaleString()}</td>
                              </tr>
                            ))}
                            {salesData.salesTaxInvoices.length === 0 && (
                              <tr><td colSpan="5" className="text-center text-muted py-3">No sales tax invoices</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <div className="card mt-4">
        <div className="card-header">
          <h5 className="mb-0">Export Reports</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <h6>Current Report Export</h6>
              <button
                className="btn btn-primary me-2"
                onClick={() => {
                  if (activeReport === 'sales') handleExportSales();
                  else if (activeReport === 'tenders') handleExportTendersCSV();
                  else if (activeReport === 'quotations') handleExportQuotations('CSV');
                  else if (activeReport === 'pnl') handleExportPnL('CSV');
                  else if (activeReport === 'stock') handleExportStock('CSV');
                  else Swal.fire('Info', 'Export not implemented for this report type', 'info');
                }}
              >
                <i className="bi bi-file-earmark-excel"></i> Export to Excel
              </button>
              <button
                className="btn btn-danger"
                onClick={() => {
                  if (activeReport === 'tenders') handleExportTendersPDF();
                  else if (activeReport === 'sales') handleExportSalesPDF();
                  else if (activeReport === 'quotations') handleExportQuotations('PDF');
                  else if (activeReport === 'pnl') handleExportPnL('PDF');
                  else if (activeReport === 'stock') handleExportStock('PDF');
                  else Swal.fire('Info', 'PDF Export not implemented for this report type', 'info');
                }}
              >
                <i className="bi bi-file-earmark-pdf"></i> Export to PDF
              </button>
            </div>
            <div className="col-md-6 mb-3">
              <h6>Bulk Export</h6>
              <button
                className="btn btn-success me-2"
                onClick={handleExportAll}
              >
                <i className="bi bi-file-earmark-zip"></i> Export All (Zip)
              </button>
              <button
                className="btn btn-warning"
                style={{ opacity: 0.5, cursor: 'not-allowed' }}
                title="Use Zip Export"
              >
                <i className="bi bi-file-earmark-pdf"></i> Export All (PDF)
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;

